import { NormalizedTextBox, TextFocusSelector } from '../text-focus.service';

/**
 * The canonical in-application shape of one piece of report evidence (Issue #1773).
 *
 * This model is deliberately independent of the protobuf wire shape. The wire shape is
 * append-only and full of "unset means unknown" ambiguity, because it has to stay compatible with
 * reports cached before #1773. This model is the opposite: by the time a citation reaches a
 * template it has a definite {@link EvidenceCitationKind}, and every field that kind requires is
 * present and validated. Everything ambiguous is resolved once, in
 * `evidence-citation.mapper.ts`, so no component ever has to ask "is this really a region
 * citation, or just one that happens to have a region path?".
 *
 * The same model backs Compliance Reports, chat source attribution, and future project-wide and
 * exported reports, which is why it lives in `shared/` rather than under the compliance feature.
 */

/**
 * What a citation points at, and therefore how precisely it may be rendered.
 *
 * There is no "unspecified" member. A citation whose kind cannot be determined is not a citation
 * with an unknown kind, it is invalid input, and the mapper rejects it with a diagnostic rather
 * than passing a half-formed record downstream.
 */
export type EvidenceCitationKind = 'page' | 'region' | 'text' | 'code';

/** How a citation's target was arrived at, in descending order of precision it justifies. */
export type EvidenceResolutionMethod = 'deterministic' | 'agentTrace' | 'indexLookup' | 'inferred';

/** The agent's confidence in this specific citation — not in the report's overall verdict. */
export type EvidenceConfidence = 'high' | 'medium' | 'low' | 'unknown';

/**
 * A page-normalized rectangle in fractional coordinates.
 *
 * Reuses {@link NormalizedTextBox} from #1744 rather than declaring a parallel type, so a region
 * box and an OCR text box are the same shape and both can be handed to
 * `PreviewFocusService.focusTarget()` without conversion.
 */
export type NormalizedBox = NormalizedTextBox;

/** Identity of the project page a citation refers to. */
export interface CitationPageIdentity {
  /** Architectural plan (project) that owns the page. Always present on a valid citation. */
  planId: string;
  /**
   * File within the project. Legacy single-file projects have no file ID, and the route falls back
   * to file `1` exactly as chat page links and {@link FilePageLinkService} already do.
   */
  fileId: string;
  /** One-based page number. Always >= 1 on a valid citation. */
  pageNumber: number;
  /** Sheet identifier such as `A2.1`, denormalized for display. Empty when the page has none. */
  sheetId: string;
}

/** Canonical identity of a building-code provision. */
export interface CodeProvisionTarget {
  bookId: string;
  documentSlug: string;
  chapterSlug: string;
  chapterTitle: string;
  /** Fully qualified section ID, used as the URL fragment (e.g. `IBC2021P2_Ch11_Sec1105`). */
  sectionId: string;
  /**
   * The wording the link must visibly show, e.g. `CBC § 1010.1.1`.
   *
   * WCAG 2.5.3 requires the accessible name to start with this, so a voice user who speaks the
   * visible label activates the link. Context may be appended, never substituted.
   */
  visibleLabel: string;
  /**
   * The provision's own title from the book's table of contents, when the page had one loaded.
   *
   * Optional on purpose: a report can name a subsection that is not in the loaded contents at all,
   * and the tooltip then states the number alone rather than inventing a name for it.
   */
  sectionTitle?: string;
  /**
   * True when this citation is a code reference the report's prose already names inline.
   *
   * Marks the one surface whose tooltip wording changed in Issue #1773. Header chips and the sources
   * list keep the previous wording, so the flag is what keeps that change contained rather than a
   * check on an ID prefix, which would be a guess about the same thing.
   */
  proseInline?: boolean;
}

/** How a citation's evidence was resolved, and against which artifact revisions. */
export interface EvidenceProvenance {
  method: EvidenceResolutionMethod;
  /** Model behind the finding. Empty when the target was derived with no model judgment. */
  generatedByModel: string;
  /** Page artifact revision the evidence was resolved against. Empty when not recorded. */
  pageArtifactRevision: string;
  /** Region hierarchy revision the region path was resolved against. Empty when not recorded. */
  regionHierarchyRevision: string;
  resolvedTime?: Date;
}

/** Fields every citation carries regardless of kind. */
interface EvidenceCitationBase {
  /**
   * Stable ID a prose marker refers to. Guaranteed to match `[A-Za-z0-9_-]{1,64}` once mapped,
   * because it is substituted into DOM ids and must never need escaping.
   *
   * Legacy citations have no ID of their own. The mapper synthesizes a positional one
   * (`legacy-0`, `legacy-1`, …) so the citation is still renderable as a source chip, while
   * {@link EvidenceCitation.referenceable} records that prose cannot address it.
   */
  id: string;
  /** The claim this supports, or `''` meaning "supports the report as a whole". */
  claimId: string;
  /**
   * Whether prose markers may address this citation.
   *
   * False for legacy citations whose ID was synthesized: a marker in prose could not possibly
   * have been written against an ID this application invented after the fact, so treating such a
   * marker as a match would be inventing a link the generator never asserted.
   */
  referenceable: boolean;
  confidence: EvidenceConfidence;
  provenance: EvidenceProvenance;
  /** Short supporting excerpt for display, already length-bounded. Empty when none was recorded. */
  excerpt: string;
}

/** File and page identity only. Renders as a page link, implying no precision beyond the page. */
export interface PageEvidenceCitation extends EvidenceCitationBase {
  kind: 'page';
  page: CitationPageIdentity;
}

/**
 * A region or object in the page's region hierarchy.
 *
 * `regionPath` is the durable identity and is preferred for navigation, because the hierarchy can
 * re-resolve the current box for a path after re-ingestion. `box` is a snapshot taken at
 * generation time and is optional: it lets the UI describe the target before the hierarchy has
 * loaded, but it is never the reason a link works.
 */
export interface RegionEvidenceCitation extends EvidenceCitationBase {
  kind: 'region';
  page: CitationPageIdentity;
  regionPath: string;
  box?: NormalizedBox;
}

/**
 * An exact passage of page text.
 *
 * The selector is #1744's own contract, handed through unchanged so
 * `TextFocusService.focusSelector()` can re-resolve it against the live page index and report
 * staleness itself rather than this code second-guessing it.
 */
export interface TextEvidenceCitation extends EvidenceCitationBase {
  kind: 'text';
  page: CitationPageIdentity;
  selector: TextFocusSelector;
}

/** A building-code provision. Points outside the project entirely. */
export interface CodeEvidenceCitation extends EvidenceCitationBase {
  kind: 'code';
  code: CodeProvisionTarget;
}

/** One resolved, renderable piece of report evidence. */
export type EvidenceCitation =
  | PageEvidenceCitation
  | RegionEvidenceCitation
  | TextEvidenceCitation
  | CodeEvidenceCitation;

/** Citations that name a page in the project, i.e. everything except code provisions. */
export type PageAnchoredCitation = PageEvidenceCitation | RegionEvidenceCitation | TextEvidenceCitation;

/** Narrowing helper: whether a citation points at a page in the project. */
export function isPageAnchored(citation: EvidenceCitation): citation is PageAnchoredCitation {
  return citation.kind !== 'code';
}

/**
 * Why a candidate citation was refused.
 *
 * Every rejection is reported, never swallowed. A reader who cannot be shown a precise highlight
 * is owed an explanation, and a developer looking at a report with missing links is owed a reason
 * in the console. The member names are also the vocabulary the specs assert against, so a rule
 * cannot be quietly relaxed without a test changing.
 */
export type CitationRejectionReason =
  /** No page number, no code reference — nothing to point at. */
  | 'noTarget'
  /** `page_number` absent, zero, negative, or not an integer. */
  | 'invalidPageNumber'
  /**
   * A stated `file_id` that is not a positive integer.
   *
   * Raised by security review of PR #1824 as finding F2. It was not exploitable: the project is
   * always the report's own, and both the router link and the printable URL encode the segment. It
   * is refused anyway because this module's whole job is to validate identity, and leaving one
   * identity field unchecked in the middle of that is an inconsistency a later change could rely on.
   */
  | 'invalidFileId'
  /** Citation names a project other than the report's own. The cross-tenant guard. */
  | 'unauthorizedProject'
  /** Bounding box outside [0,1], inverted, or degenerate. */
  | 'malformedGeometry'
  /** Selector missing its `exact` text, carrying an unsupported version, or a negative occurrence. */
  | 'malformedSelector'
  /** Code reference with no usable section or document identity. */
  | 'invalidCodeReference'
  /** Citation ID present but containing characters that would need escaping. */
  | 'malformedCitationId'
  /** Two citations share an ID but point at different targets, so the ID is ambiguous. */
  | 'duplicateConflictingId'
  /** A prose marker naming an ID that no citation in the report supplies. */
  | 'unknownCitationId';

/** One refused candidate, with enough context to explain it to a reader and to a developer. */
export interface CitationRejection {
  reason: CitationRejectionReason;
  /** The citation ID involved, when the candidate had a usable one. */
  citationId: string;
  /** Developer-facing detail. Never rendered to end users verbatim. */
  detail: string;
}

/**
 * The outcome of turning one report's raw evidence into renderable citations.
 *
 * Both halves matter. `citations` drives the links; `rejections` drives the explicit "we could not
 * restore this" states the issue requires, and is asserted directly by the specs.
 */
export interface CitationSet {
  /** Accepted citations in their original evidence-chain order. */
  citations: EvidenceCitation[];
  /** Accepted citations indexed by ID, for marker resolution. Only `referenceable` ones appear. */
  byId: ReadonlyMap<string, EvidenceCitation>;
  rejections: CitationRejection[];
}

/** An empty citation set, for reports with no evidence at all. */
export const EMPTY_CITATION_SET: Readonly<CitationSet> = Object.freeze({
  citations: [],
  byId: new Map<string, EvidenceCitation>(),
  rejections: [],
});

/** Human-readable label for each evidence kind, used in chips, tooltips, and print footnotes. */
export const EVIDENCE_KIND_LABEL: Readonly<Record<EvidenceCitationKind, string>> = Object.freeze({
  page: 'Page',
  region: 'Plan object',
  text: 'Plan text',
  code: 'Building code',
});

/**
 * How precise a citation claims to be, in plain words, for the citation detail view.
 *
 * The issue requires citation labels to distinguish page-only, region, OCR-text, and code
 * evidence, and requires inferred evidence never to be dressed up as verified. This is where that
 * distinction is worded once instead of in each template.
 */
export function describePrecision(citation: EvidenceCitation): string {
  if (citation.provenance.method === 'inferred') {
    return 'Inferred evidence — this location was not directly verified.';
  }
  switch (citation.kind) {
    case 'page':
      return 'Page-level evidence — opens the page without a precise highlight.';
    case 'region':
      return 'Plan object evidence — opens the page and highlights the cited object.';
    case 'text':
      return 'Plan text evidence — opens the page and highlights the exact supporting text.';
    case 'code':
      return 'Building-code provision — opens the canonical code section.';
  }
}
